import { useMemo, useState } from "react";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
// material
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { useOpenSnackbar } from "@hooks/useSnackbar";
import axios from "@utils/axios";
import { adminMembershipsQuery, adminRefreshTick } from "@states/admin";
import { AdminUserType, MembershipGroupType } from "types/admin";
import { LEVEL_LABEL } from "./levels";

/** 서버 오류 문구. **문자열일 때만** 쓴다 — 422 의 `detail` 은 객체 배열이라 React 가 터진다. */
function errorText(error: any, fallback: string): string {
  return typeof error?.detail === "string" ? error.detail : fallback;
}

type Props = {
  user: AdminUserType | null;
  onClose: () => void;
};

export default function MembershipDialog({ user, onClose }: Props) {
  const loadable = useRecoilValueLoadable(adminMembershipsQuery);
  const bumpRefresh = useSetRecoilState(adminRefreshTick);
  const openSnackbar = useOpenSnackbar();
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<number | "">("");

  // 삼항식을 그대로 두면 매 렌더마다 새 배열이 되어 아래 `useMemo` 들이 무의미해진다.
  // 스프린트 4 의 `SignupRequestPanel` 에서 같은 것을 겪었다.
  const groups = useMemo<MembershipGroupType[]>(
    () => (loadable.state === "hasValue" ? loadable.contents : []),
    [loadable.state, loadable.contents],
  );

  // 이미 가진 것은 고를 수 없게 한다. 서버가 멱등이라 눌러도 탈은 없지만,
  // 고를 수 있게 두면 "눌렀는데 아무 일도 안 일어난다" 로 보인다.
  const held = useMemo(() => new Set((user?.memberships ?? []).map((m) => m.name)), [user]);
  const available = useMemo(
    () => groups.filter((group) => !held.has(group.name)),
    [groups, held],
  );

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      openSnackbar(done, "success");
      bumpRefresh((tick) => tick + 1);
      onClose();
    } catch (error: any) {
      openSnackbar(errorText(error, "처리하지 못했습니다. 잠시 후 다시 시도해주세요."), "error");
      bumpRefresh((tick) => tick + 1);
    } finally {
      setBusy(false);
    }
  };

  const grant = () =>
    run(
      () => axios.post(`users/${user!.id}/memberships/${picked}`),
      "멤버십을 부여했습니다.",
    );

  const revoke = (groupId: number, groupName: string) =>
    run(
      () => axios.delete(`users/${user!.id}/memberships/${groupId}`),
      `${groupName} 을(를) 회수했습니다.`,
    );

  // 이름으로 찾는다. `UserDTO.memberships` 에 `id` 가 없어서다(이름·등급·기간만 준다).
  //
  // 이게 안전한 이유는 `memberships.name` 에 **유니크 제약**이 걸려 있기 때문이다
  // (마이그레이션 `9a4f1c2b7e83`). 그 제약을 풀면 여기가 조용히 틀린 그룹을 회수한다.
  // 둘은 같이 움직인다.
  const byName = useMemo(
    () => new Map(groups.map((group) => [group.name, group])),
    [groups],
  );

  return (
    <Dialog open={Boolean(user)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        멤버십 관리
        <Typography variant="body2" color="text.secondary">
          {user?.name ? `${user.name} · ` : ""}
          {user?.email}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">현재 멤버십</Typography>
            {(user?.memberships ?? []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                없습니다. 등급이 없으면 데이터 화면이 열리지 않습니다.
              </Typography>
            ) : (
              (user?.memberships ?? []).map((membership) => {
                const group = byName.get(membership.name);
                const label = membership.level ? LEVEL_LABEL[membership.level] : null;
                return (
                  <Stack
                    key={membership.name}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{membership.name}</Typography>
                      {label && <Chip size="small" label={label.text} color={label.color} />}
                    </Stack>
                    <Button
                      size="small"
                      color="error"
                      disabled={busy || !group}
                      onClick={() => revoke(group!.id, membership.name)}
                    >
                      회수
                    </Button>
                  </Stack>
                );
              })
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">멤버십 부여</Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                select
                size="small"
                fullWidth
                label="그룹"
                value={picked}
                onChange={(event) => setPicked(Number(event.target.value))}
              >
                {available.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                    {group.level ? ` (${LEVEL_LABEL[group.level].text})` : ""}
                  </MenuItem>
                ))}
              </TextField>
              <LoadingButton
                variant="contained"
                loading={busy}
                disabled={picked === ""}
                onClick={grant}
              >
                부여
              </LoadingButton>
            </Stack>
            {/* 관리자를 늘리는 것은 관리자의 정당한 권한이지만, 모르고 누르면 안 된다. */}
            {picked !== "" &&
              groups.find((group) => group.id === picked)?.level === "admin" && (
                <Typography variant="caption" color="warning.main">
                  ⚠️ 관리자 등급입니다. 이 사용자가 관리 화면 전체를 쓸 수 있게 됩니다.
                </Typography>
              )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}
